<template>
    <a-modal
        title="导出追问数据"
        v-model:open="open"
        class="full-modal"
        :width="'70vw'"
        :body-style="{ maxHeight: '95vh', overflowY: 'auto' }"
        :footer="null"
        @cancel="handleCancel"
    >
        <!-- 查询条件 -->
        <div class="export-search-bar">
            <div class="export-search-item">
                <a>问题关键词：</a>
                <a-input v-model:value="queryKeyword" placeholder="请输入问题关键词" class="export-search-input" />
            </div>
            <div class="export-search-actions">
                <a-button type="primary" @click="onSearch" :icon="h(SearchOutlined)">搜索</a-button>
                <a-button @click="onReset" :icon="h(RedoOutlined)">重置</a-button>
            </div>
        </div>

        <!-- 数据表格 -->
        <a-table
            :columns="columns"
            :data-source="data"
            :pagination="false"
            row-key="id"
            :row-selection="{
                selectedRowKeys: selectedRowKeys,
                onChange: onSelectChange,
            }"
            :loading="loading"
            size="middle"
            bordered
            class="export-table"
        >
            <template #bodyCell="{ column, text }">
                <template v-if="column.key === 'keyword' || column.key === 'followContent'">
                    <a-tooltip :title="text">
                        <span>{{ text && text.length > 20 ? text.slice(0, 20) + '...' : text }}</span>
                    </a-tooltip>
                </template>
                <template v-if="column.key === 'caseNo'">
                    <a-tag color="blue">{{ text }}</a-tag>
                </template>
                <template v-if="column.key === 'parentId'">
                    <a-tag v-if="!text" color="green">主追问</a-tag>
                    <a-tag v-else color="orange">子追问</a-tag>
                </template>
                <template v-if="column.key === 'status'">
                    <a-tag v-if="text === '正常'" color="green">{{ text }}</a-tag>
                    <a-tag v-else color="red">{{ text }}</a-tag>
                </template>
            </template>
        </a-table>

        <!-- 分页 -->
        <div class="export-pagination-bar">
            <span class="export-selected-info">已选择 <strong style="color: #1890ff;">{{ selectedRowKeys.length }}</strong> 条数据</span>
            <a-pagination
                :current="pagination.current"
                :total="pagination.total"
                :page-size="pagination.pageSize"
                show-size-changer
                show-quick-jumper
                :show-total="(total, range) => `第 ${range[0]}-${range[1]} 条，共 ${total} 条`"
                @change="handlePaginationChange"
                @show-size-change="handleSizeChange"
                size="small"
            />
        </div>

        <!-- 底部按钮 -->
        <div class="export-footer">
            <a-button @click="handleCancel">取消</a-button>
            <a-button type="primary" :loading="exporting" :disabled="selectedRowKeys.length === 0" @click="handleExport">
                <template #icon><DownloadOutlined /></template>
                导出选中数据 ({{ selectedRowKeys.length }})
            </a-button>
        </div>
    </a-modal>
</template>

<script>
import { followQuestionPage, exportFollowQuestion } from '@/api/service';
import { SearchOutlined, RedoOutlined, DownloadOutlined } from '@ant-design/icons-vue';
import { message } from 'ant-design-vue';
import { ref, h } from 'vue';

export default {
    name: 'exportFollowComponent',
    components: {},
    setup() {
        const open = ref(false);
        const loading = ref(false);
        const exporting = ref(false);
        const data = ref([]);
        const queryKeyword = ref('');
        const selectedRowKeys = ref([]);
        const pagination = ref({
            current: 1,
            pageSize: 10,
            total: 0,
        });

        const columns = [
            { title: '问题关键词', dataIndex: 'keyword', key: 'keyword', width: '15%' },
            { title: '追问轮次', dataIndex: 'followRound', key: 'followRound', width: '8%' },
            { title: '追问类型', dataIndex: 'parentId', key: 'parentId', width: '8%' },
            { title: '追问内容', dataIndex: 'followContent', key: 'followContent', width: '20%' },
            { title: '关联案例', dataIndex: 'caseNo', key: 'caseNo', width: '12%' },
            { title: '状态', dataIndex: 'status', key: 'status', width: '8%' },
            { title: '追问人', dataIndex: 'createBy', key: 'createBy', width: '10%' },
            { title: '追问时间', dataIndex: 'createdAt', key: 'createdAt', width: '12%' },
        ];

        const loadData = async () => {
            loading.value = true;
            const response = await followQuestionPage({
                pageNumber: pagination.value.current,
                pageSize: pagination.value.pageSize,
                keyword: queryKeyword.value || undefined,
                parent: '是',
            });
            if (response.code === 200) {
                pagination.value.total = response.totalRow || response.data.totalRow || response.data.total || 0;
                data.value = (response.data.records || []).map(item => ({
                    id: item.id,
                    caseNo: item.caseNo,
                    keyword: item.keyword,
                    followRound: item.followRound,
                    followContent: item.followContent,
                    status: item.status,
                    createBy: item.createBy,
                    createdAt: item.createdAt,
                    parentId: item.parentId,
                }));
            }
            loading.value = false;
        };

        const openExport = () => {
            open.value = true;
            queryKeyword.value = '';
            selectedRowKeys.value = [];
            pagination.value.current = 1;
            loadData();
        };

        const onSelectChange = (keys) => {
            selectedRowKeys.value = keys;
        };

        const onSearch = () => {
            pagination.value.current = 1;
            loadData();
        };

        const onReset = () => {
            queryKeyword.value = '';
            pagination.value.current = 1;
            loadData();
        };

        const handlePaginationChange = (page, pageSize) => {
            pagination.value.current = page;
            pagination.value.pageSize = pageSize;
            loadData();
        };

        const handleSizeChange = (current, size) => {
            pagination.value.current = 1;
            pagination.value.pageSize = size;
            loadData();
        };

        const handleCancel = () => {
            open.value = false;
        };

        const handleExport = async () => {
            if (selectedRowKeys.value.length === 0) {
                message.warning('请先选择要导出的数据');
                return;
            }
            exporting.value = true;
            try {
                const blob = await exportFollowQuestion(selectedRowKeys.value);
                // 判断是否为 JSON 错误响应
                if (blob.type && blob.type.includes('json')) {
                    const json = await blob.text();
                    const result = JSON.parse(json);
                    message.error('导出失败' + (result.msg || ''));
                } else {
                    // 触发文件下载
                    const url = window.URL.createObjectURL(blob);
                    const link = document.createElement('a');
                    link.href = url;
                    link.setAttribute('download', '追问数据.xlsx');
                    document.body.appendChild(link);
                    link.click();
                    window.URL.revokeObjectURL(url);
                    document.body.removeChild(link);
                    message.success('导出成功');
                    open.value = false;
                }
            } catch (e) {
                message.error('导出失败：' + e.message);
            }
            exporting.value = false;
        };

        return {
            open,
            loading,
            exporting,
            data,
            queryKeyword,
            selectedRowKeys,
            pagination,
            columns,
            openExport,
            onSelectChange,
            onSearch,
            onReset,
            handlePaginationChange,
            handleSizeChange,
            handleCancel,
            handleExport,
            h,
            SearchOutlined,
            RedoOutlined,
            DownloadOutlined,
        };
    },
};
</script>

<style scoped>
.export-search-bar {
    display: flex;
    flex-wrap: wrap;
    gap: 12px;
    align-items: flex-end;
    margin-bottom: 16px;
    padding-bottom: 16px;
    border-bottom: 1px solid #f0f0f0;
}

.export-search-item {
    display: flex;
    flex-direction: column;
    gap: 4px;
}

.export-search-item a {
    font-size: 13px;
    font-weight: 500;
    color: rgba(0, 0, 0, 0.65);
}

.export-search-input {
    width: 180px;
}

.export-search-actions {
    display: flex;
    gap: 8px;
    align-items: flex-end;
    padding-bottom: 1px;
}

.export-table {
    margin-bottom: 12px;
}

.export-pagination-bar {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 16px;
}

.export-selected-info {
    font-size: 13px;
    color: rgba(0, 0, 0, 0.65);
}

.export-footer {
    display: flex;
    justify-content: flex-end;
    gap: 8px;
    padding-top: 12px;
    border-top: 1px solid #f0f0f0;
}
</style>
